import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { RewardCardDetailsProps } from "./RewardCardDetails.types";
import { Box } from "../../../primitives/Box/Box";
import { Flex } from "../../../primitives/Flex/Flex";
import { DetailsContainer, SpinnerContainer } from "./RewardCardDetails.styles";
import { ButtonSpinner } from "../ButtonSpinner/ButtonSpinner";
import { triggerGa } from "../../../../utils/gaUtils";
import {
  GA_ACTION,
  GA_CATEGORIES,
  GA_PAGE,
  GA_SCREEN_NAME,
  IMAGE_PREFIX,
} from "../../../../utils/constants";
import { openExternalLinkInNewTab } from "../../../../utils/common";
import { useGetCreatorDataWithCreatorIdQuery } from "../../../../Queries/useGetCreatorDataWithCreatorIdQuery";
import { useWebengageEventTriggerMutation } from "../../../../redux/reduxAPI.js";

export const RewardCardDetails: React.FC<RewardCardDetailsProps> = ({
  optedIn,
  startDateTime,
  endDateTime,
  rewardAmount,
  rewardId,
  rewardBanner,
  rewardExpiredBanner,
  rewardRedirectUrl,
  rewardName,
  rewardDisplayName,
  handleOptIn,
  daysLeft,
  isContestOver,
  isContestUpcoming,
  firstSaleCompleted,
  position,
  optedCreatorsCount,
  isRewardRejected,
  isRewardApproved,
  dataCy,
}) => {
  const dateToday = new Date();
  const navigate = useNavigate();
  const { data: creatorDetails } = useGetCreatorDataWithCreatorIdQuery();
  const [isOptedInClicked, setIsOptedInClicked] = useState(false);
  const [triggerWebengageEvent] = useWebengageEventTriggerMutation();

  const gaTrigger = ({ action }: { action: string }) => {
    triggerGa({
      category: GA_CATEGORIES.NAVIGATION,
      action: action,
      label: `${rewardName}`,
      label2: `${
        isContestOver ? "Past" : isContestUpcoming ? "Upcoming" : "Live"
      }`,
      label3: `${
        !firstSaleCompleted
          ? "Locked"
          : rewardRedirectUrl
          ? "External"
          : optedIn
          ? "Opted In"
          : "Not Opted In"
      }`,
      custom: `${
        !firstSaleCompleted
          ? "locked"
          : rewardRedirectUrl
          ? "NA"
          : isContestOver
          ? rewardAmount > 0
            ? "pastwon"
            : "pastnotwon"
          : isContestUpcoming
          ? "upcoming"
          : rewardAmount > 0
          ? "livewon"
          : "livenotwon"
      }`,
      page: GA_PAGE.WISHLINK_REWARDS,
      screenName: GA_SCREEN_NAME.WISHLINK_REWARDS,
      eventValue: position,
    });
  };

  const handleTrackProgress = () => {
    navigate(`/wishlink-rewards/${rewardId}?tab=track-my-progress`);
    triggerWebengageEvent({
      event_source: "Reward clicked",
      event_value: rewardId,
    });
    gaTrigger({ action: GA_ACTION.TRACK_PROGRESS });
  };

  const handleDetailsClick = () => {
    navigate(`/wishlink-rewards/${rewardId}`);
    triggerWebengageEvent({
      event_source: "Reward clicked",
      event_value: rewardId,
    });
    gaTrigger({ action: GA_ACTION.VIEW_DETAILS });
  };

  const handleCheckProgress = rewardRedirectUrl
    ? () => {
        openExternalLinkInNewTab({
          redirectUrl: `${rewardRedirectUrl}?username=${creatorDetails?.username}`,
        });
        triggerWebengageEvent({
          event_source: "Reward clicked",
          event_value: rewardId,
        });
        gaTrigger({ action: GA_ACTION.TRACK_PROGRESS });
      }
    : () => {};

  let buttonBgColor = isRewardRejected
    ? "background.danger.default"
    : rewardRedirectUrl
    ? "background.default.default"
    : rewardAmount > 0
    ? "background.positive.default"
    : optedIn && rewardAmount === 0
    ? "background.warning.default"
    : "background.danger.tertiary";

  let buttonText = isRewardRejected
    ? "You did not qualify for the Reward"
    : rewardRedirectUrl
    ? "Track Progress"
    : rewardAmount > 0
    ? `🎉 Congratulations! You Won ₹ ${rewardAmount}`
    : "🥹 Ooops! You just missed this Reward";

  let buttonText2 = !optedIn
    ? "Opt In"
    : new Date(startDateTime) > dateToday
    ? "Opted In"
    : "Track Progress";

  return (
    <DetailsContainer data-cy={dataCy}>
      <Flex
        justifyContent="flex-start"
        alignItems="flex-start"
        flexDirection="column"
        width="100%"
        height="100%"
        p={firstSaleCompleted ? "12px 12px" : "0px 16px"}
      >
        {isRewardRejected ||
        (isContestOver &&
          (!optedIn || isRewardApproved || (optedIn && rewardAmount === 0))) ||
        (rewardRedirectUrl && firstSaleCompleted) ? (
          <Box
            as="button"
            fontSize="12px"
            fontWeight={400}
            lineHeight="18px"
            fontFamily="Lexend"
            borderRadius="8px"
            backgroundColor={buttonBgColor}
            border={rewardRedirectUrl ? "1px solid" : "none"}
            borderColor={
              rewardRedirectUrl ? "background.wishlink.default" : undefined
            }
            color={
              isRewardRejected
                ? "white.1000"
                : rewardRedirectUrl
                ? "background.wishlink.default"
                : rewardAmount > 0
                ? "white.1000"
                : "black.1000"
            }
            textAlign="center"
            width="296px"
            height="32px"
            px="8px"
            py="6px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor="pointer"
            onClick={handleCheckProgress}
          >
            {buttonText}
          </Box>
        ) : !firstSaleCompleted ? (
          <Flex p="0.25rem" pt="0.5rem">
            <Box
              color="text.default.primary"
              fontSize="10px"
              fontWeight={400}
              lineHeight="10px"
              backgroundColor="white.1000"
              p="5px"
            >
              <Flex alignItems="center" mr="4px">
                <Box
                  as="img"
                  src={`${IMAGE_PREFIX}unlock-icon.png`}
                  alt=""
                  width="10px"
                  height="10px"
                />
                Unlock the rewards by
              </Flex>
              <Box as="ul" pl="24px" color="rgba(0, 0, 0, 0.75)">
                <Box as="li">Share Wishlinks on social media</Box>
                <Box as="li">
                  Once you have a sale on any product, your rewards will be
                  unlocked
                </Box>
              </Box>
            </Box>
          </Flex>
        ) : (
          <Flex alignItems="center">
            <Flex justifyContent="flex-start" gap="10px" width="100%">
              <Box
                as="button"
                borderRadius="8px"
                border="1px solid"
                borderColor="background.wishlink.default"
                backgroundColor="white.1000"
                width="142px"
                height="32px"
                py="7px"
                px="8px"
                textAlign="center"
                cursor="pointer"
                display="flex"
                alignItems="center"
                justifyContent="center"
                onClick={handleDetailsClick}
              >
                <Box
                  color="background.wishlink.default"
                  fontSize="12px"
                  fontWeight={500}
                  lineHeight="18px"
                  fontFamily="Rubik"
                >
                  Details
                </Box>
              </Box>

              <Box
                as="button"
                borderRadius="8px"
                backgroundColor={
                  buttonText2 === "Track Progress"
                    ? "background.positive.default"
                    : "background.wishlink.default"
                }
                width="142px"
                height="32px"
                py="7px"
                px="8px"
                textAlign="center"
                cursor="pointer"
                border="none"
                display="flex"
                alignItems="center"
                justifyContent="center"
                onClick={
                  optedIn
                    ? new Date(startDateTime) > dateToday
                      ? () => {}
                      : handleTrackProgress
                    : () => {
                        setIsOptedInClicked(true);
                        handleOptIn();
                        setTimeout(() => {
                          navigate(`/wishlink-rewards/${rewardId}`);
                          setIsOptedInClicked(false);
                        }, 3000);
                      }
                }
                id={`${buttonText2}-${rewardName}`}
              >
                {isOptedInClicked ? (
                  <SpinnerContainer>
                    <ButtonSpinner size="0.2rem" color="white.1000" />
                  </SpinnerContainer>
                ) : (
                  <Box
                    fontSize="12px"
                    fontWeight={500}
                    lineHeight="18px"
                    color="white.1000"
                    fontFamily="Rubik"
                  >
                    {buttonText2}
                  </Box>
                )}
              </Box>
            </Flex>
          </Flex>
        )}
        {!rewardRedirectUrl &&
          optedCreatorsCount > 10 &&
          firstSaleCompleted && (
            <Flex alignItems="center" mt="10px" height="17px">
              <Box
                as="img"
                src={`${IMAGE_PREFIX}creators_icon.png`}
                alt=""
                height="17px"
                mr="0.2rem"
              />
              <Box
                color="text.default.primary"
                fontSize="11px"
                fontWeight={400}
                lineHeight="17px"
                fontFamily="Rubik"
              >
                {optedCreatorsCount} Creators are Participating
              </Box>
            </Flex>
          )}
      </Flex>
    </DetailsContainer>
  );
};
